import {
  AuthenticationsOrder,
  AuthenticationsStatus,
  MultiFilterOperator,
  OwnerStatus,
} from "@zapier/bff";
import { RouterOutputs, trpc } from "../trpc";
import { keepPreviousData } from "@tanstack/react-query";

export type Authentication =
  RouterOutputs["authentication"]["authentications"]["results"][number];

export interface Input {
  excludeUnusable?: boolean;
  includeCompatible?: boolean;
  includeZapCount?: boolean;
  ids?: string[];
  isManaged?: boolean;
  isManagedAuthViolation?: boolean;
  isRestricted?: boolean;
  limit?: number;
  offset?: number;
  ordering?: AuthenticationsOrder;
  owner?: string;
  owners?: string;
  ownersOperator?: MultiFilterOperator;
  ownerStatus?: OwnerStatus;
  search?: string;
  selectedApi?: string;
  apps?: string;
  appsOperator?: MultiFilterOperator;
  serviceSlug?: string;
  shared?: boolean;
  sharedByMe?: boolean;
  sharedWithAll?: boolean;
  stale?: boolean;
  status?: AuthenticationsStatus;
}

interface Options {
  enabled?: boolean;
}

// Owner param supports a customuserId, or alternately the special values "me"
// or "!me" to filter owner by current user or not owned by current user, respectively.

export function useQueryAuthentications(
  {
    excludeUnusable,
    includeCompatible,
    includeZapCount = false,
    ids,
    isManaged,
    isManagedAuthViolation,
    isRestricted,
    limit = 25,
    offset = 0,
    ordering,
    owner,
    owners,
    ownersOperator,
    ownerStatus,
    search,
    selectedApi,
    apps,
    appsOperator,
    serviceSlug,
    shared,
    sharedByMe,
    sharedWithAll,
    status,
  }: Input,
  { enabled = true }: Options = {},
) {
  const { data, isFetching, isLoading } =
    trpc.authentication.authentications.useQuery(
      {
        excludeUnusable,
        includeCompatible,
        includeZapCount,
        ids,
        isManaged,
        isManagedAuthViolation,
        isRestricted,
        limit,
        offset,
        ordering,
        owner,
        owners,
        ownersOperator,
        ownerStatus,
        search,
        selectedApi,
        apps,
        appsOperator,
        serviceSlug,
        shared,
        sharedByMe,
        sharedWithAll,
        status,
      },
      {
        enabled,
        placeholderData: keepPreviousData,
        // Disable focus-triggered refetches. Opening the OAuth reconnect popup
        // causes a focus/blur cycle on the main window; closing it (success,
        // dismissal, or error) returns focus, which would otherwise fire a
        // stale-data refetch before we can determine whether the reconnect
        // succeeded. The explicit refetchAuthentications() call in the reconnect
        // flow handles all intentional cache invalidations instead.
        // Trade-off: this list no longer auto-refreshes on window focus for
        // general staleness either (e.g. a connection edited in another tab).
        // Any caller relying on that behavior must trigger an explicit refetch.
        refetchOnWindowFocus: false,
        trpc: {
          context: {
            // Skip batching to avoid bundling this critical procedure with slower requests
            skipBatching: true,
          },
        },
      },
    );

  return {
    totalCount: data?.count || 0,
    authentications: (data?.results || []) as Authentication[],
    isLoading,
    isFetching,
  };
}
